import { computed, shallowRef, watch, type Ref } from 'vue'
import { Statuses } from '@/enum/redesignStatusTypes'
import {
    getSelectedAccountStatusById,
    selectedAccountBreaks
} from '@/composables/globalData/useEntitiesData'
import { allRealtimeExtensions } from '@/composables/useStoreExtensions'
import QueueAgentsDataService, { type StoredQueueMember } from '@/core/services/QueueAgentsDataService'
import store from '@/store/store'
import type { Extension } from '@/types/extension'

export interface QueueAgentInfo {
    UserID: number
    UserName: string
}
export interface ProcessedQueues {
    allAvailable: QueueAgentInfo[]
    allUnavailable: QueueAgentInfo[]
}

const EMPTY_PROCESSED: ProcessedQueues = {
    allAvailable: [],
    allUnavailable: []
}

const processedQueuesRef = shallowRef<ProcessedQueues>(EMPTY_PROCESSED)
let rebuildScheduled = false

function isMemberAvailable (member: StoredQueueMember, extensions: Extension[] | undefined): boolean {
    if (!extensions?.length) return false

    const firstExt = extensions[0]
    if (firstExt.representativeStatus === Statuses.LOGOUT) return false

    if (member.IsLoginRequired) {
        const status = getSelectedAccountStatusById(firstExt.representativeStatus)
        if (!status || status.GetQueueCalls !== 1) return false
    }

    return !extensions.some(ext => ext.calls.length > 0)
}

function rebuild (): void {
    const queues = QueueAgentsDataService.data.value
    const members = QueueAgentsDataService.allMembers.value
    if (!queues || !members.length) {
        processedQueuesRef.value = EMPTY_PROCESSED
        return
    }

    const extByUser = new Map<number, Extension[]>()
    for (const ext of allRealtimeExtensions.value ?? []) {
        let bucket = extByUser.get(ext.representative)
        if (!bucket) {
            bucket = []
            extByUser.set(ext.representative, bucket)
        }
        bucket.push(ext)
    }

    const allAvailable: QueueAgentInfo[] = []
    const allUnavailable: QueueAgentInfo[]= []

    for (const member of members) {
        const ext  = extByUser.get(member.UserID)
        if (isMemberAvailable(member, ext)) {
            allAvailable.push(member)
        } else {
            allUnavailable.push(member)
        }
    }

    processedQueuesRef.value = {
        allAvailable: allAvailable,
        allUnavailable: allUnavailable
    }
}
// Coalesce bursts (shift change, mass logout) into one rebuild per microtask
// instead of one per mutation. Microtask runs right after the current sync
// block finishes, so no user-perceived delay.
function scheduleRebuild (): void {
    if (rebuildScheduled) return
    rebuildScheduled = true
    Promise.resolve().then(() => {
        rebuildScheduled = false
        rebuild()
    })
}

// Subscribe at the Vuex mutation layer rather than via watch(allRealtimeExtensions).
// The SDK mutates its internal array in place (extensions.splice + ft.upsert with
// stable ft.array reference), so a default watch never fires on UPDATE_EXTENSIONS
// — and a deep watch would reintroduce exactly the per-event reactive walk this
// file exists to eliminate.
store.subscribe((mutation) => {
    if (
        mutation.type === 'extensions/UPDATE_EXTENSIONS' ||
        mutation.type === 'extensions/SET_EXTENSIONS'
    ) {
        scheduleRebuild()
    }
})

watch(QueueAgentsDataService.data, scheduleRebuild, { immediate: true })
watch(selectedAccountBreaks, scheduleRebuild)

export const processedQueues: Readonly<Ref<ProcessedQueues>> = processedQueuesRef

export const allMembersMap = computed(() => {
    return QueueAgentsDataService.data.value
})
export function startAgentTracking (uid: string): void {
    QueueAgentsDataService.startTracking(uid)
}

export function stopAgentTracking (uid: string): void {
    QueueAgentsDataService.stopTracking(uid)
}
